import { useEffect, useState } from 'react';
import { Box, Button, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import ErrorMessage from '@/components/errorMessage';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import ProgressIndicator from '../progressIndicator';
import TabPanel from '../tabPanel';
import ConsumedEvents from './consumedEvents';
import EmittedEventDefs from './emittedEventDefs';
import SettingDefs from './settingDefs';
import { EntityType } from '../entities/entityType';
import { eventNameEquals } from '@/utils';

const implementationStacks = ['java', 'python'];

type Props = {};

const AgentDef = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { toast } = useNotificationContext();

  const { agentDefId } = useParams();

  const [loading, setLoading] = useState(false);
  const [id, setId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [domainId, setDomainId] = useState<string>(user?.domainId || 'general');
  const [domains, setDomains] = useState<any[]>([]);
  const [description, setDescription] = useState<string>('');
  const [implementationStack, setImplementationStack] = useState<string>('');
  const [implementationClass, setImplementationClass] = useState<string>('');
  const [instanceIcon, setInstanceIcon] = useState<string>('');
  const [consumedEvents, setConsumedEvents] = useState<any[]>([]);
  const [settingDefs, setSettingDefs] = useState<any>([]);
  const [emittedEventDefs, setEmittedEventDefs] = useState<any[]>([]);

  const [selectedTab, setSelectedTab] = useState<any>('consumedEvents');
  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const doms = await apiContext.getTenantDomains();
        setDomains(doms || []);
      } catch (err) {
        // ignore
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    const getAgentDef = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getAgentDef(agentDefId as string);
        setId(response.id);
        setName(response.name);
        setDomainId(response.domainId || user?.domainId || 'general');
        setDescription(response.description);
        setImplementationStack(response.implementationStack);
        setImplementationClass(response.implementationClass);
        setInstanceIcon(response.instanceIcon);
        setConsumedEvents(response.consumedEvents);
        setSettingDefs(response.settingDefs);
        setEmittedEventDefs(response.emittedEventDefs);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (agentDefId) {
      getAgentDef();
    }
  }, [agentDefId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const addConsumedEvent = (consumedEvent: any) => {
    setConsumedEvents([...consumedEvents, consumedEvent]);
  };

  const deleteConsumedEvent = (consumedEvent: any) => {
    setConsumedEvents(
      consumedEvents.filter(
        (ce) =>
          ce.entityType !== consumedEvent.entityType ||
          ce.entityDefId !== consumedEvent.entityDefId ||
          ce.entityId !== consumedEvent.entityId ||
          ce.eventDefId !== consumedEvent.eventDefId,
      ),
    );
  };

  const addCustomEvent = (customEvent: any) => {
    setEmittedEventDefs([
      ...emittedEventDefs,
      { entityDefId: id, entityType: EntityType.agentInstance, ...customEvent, eventType: 'CustomEvents' },
    ]);
  };

  const deleteCustomEvent = (customEvent: any) => {
    setEmittedEventDefs(
      emittedEventDefs.filter((ce) => !eventNameEquals(ce.eventName, customEvent.eventName) || ce.eventType !== 'CustomEvents'),
    );
  };

  const addSettingDef = (sectionName: string, settingDef: any) => {
    const section = settingDefs.find((sd: any) => sd.sectionName === sectionName);
    if (section) {
      section.items = [settingDef, ...section.items];
      setSettingDefs([...settingDefs]);
    }
  };

  const updateSettingDef = (sectionName: string, settingDef: any) => {
    const section = settingDefs.find((sd: any) => sd.sectionName === sectionName);
    if (section) {
      section.items = section.items.map((item: any) => (item.name === settingDef.name ? settingDef : item));
      setSettingDefs([...settingDefs]);
    }
  };

  const deleteSettingDef = (sectionName: string, settingDef: any) => {
    const section = settingDefs.find((sd: any) => sd.sectionName === sectionName);
    if (section) {
      section.items = section.items.filter((item: any) => item.name !== settingDef.name);
      setSettingDefs([...settingDefs]);
    }
  };

  const onAddNewSection = (sectionName: string) => {
    const section = settingDefs.find((sd: any) => sd.sectionName === sectionName);
    if (!section) {
      setSettingDefs([{ sectionName, items: [] }, ...settingDefs]);
    }
  };

  const onUpdateSectionName = (oldSectionName: string, newSectionName: string) => {
    const section = settingDefs.find((sd: any) => sd.sectionName === oldSectionName);
    if (section) {
      section.sectionName = newSectionName;
      setSettingDefs([...settingDefs]);
    }
  };

  const onDeleteSection = (sectionName: string) => {
    setSettingDefs(settingDefs.filter((section: any) => section.sectionName !== sectionName));
  };

  const submitAgent = async () => {
    const errors = {} as any;
    let hasError = false;

    if (!id) {
      errors['id'] = 'Required';
      hasError = true;
    }
    if (!name) {
      errors['name'] = 'Required';
      hasError = true;
    }
    if (!implementationStack) {
      errors['implementationStack'] = 'Required';
      hasError = true;
    }
    if (!implementationClass) {
      errors['implementationClass'] = 'Required';
      hasError = true;
    }

    setErrors(errors);
    if (!hasError) {
      try {
        setLoading(true);
        const record = {
          id,
          entityType: EntityType.agentDef,
          domainId: domainId || 'general',
          name,
          description,
          implementationStack,
          implementationClass,
          instanceIcon,
          settingDefs,
          consumedEvents,
          emittedCustomEvents: emittedEventDefs
            .filter((eed) => eed.eventType === 'CustomEvents')
            .map((ce) => ({ eventName: ce.eventName, eventLabel: ce.eventLabel })),
        };
        agentDefId ? await apiContext.updateAgentDef(id, record) : await apiContext.createAgentDef(record);
        toast({ message: 'Changes saved' });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteAgentDef = async () => {
    confirm({ description: 'Are you sure you want to delete this agent def?' }).then(async () => {
      try {
        setLoading(true);
        await apiContext.deleteAgentDef(id);
        navigate('/administration?adminTab=agents');
      } catch (error) {
        setError(error);
      }
      setLoading(false);
    });
  };

  const close = () => {
    navigate('/administration?adminTab=agents');
  };

  if (loading && !id) {
    return <ProgressIndicator />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header Bar */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4, lg: 2 }}>
            <TextField label="Id" value={id} required error={errors.id} onChange={setId} type="text" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <TextField
              label="Name"
              value={name}
              required
              error={errors.name}
              onChange={setName}
              type="text"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <TextField
              label={'Description'}
              value={description}
              multiline
              maxRows={2}
              error={errors.description}
              onChange={setDescription}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <SelectField
              label="Domain"
              options={[
                { label: 'General (All domains)', value: 'general' },
                ...domains.map((d: any) => ({ label: `${d.name} (${d.id})`, value: d.id })),
              ]}
              value={domainId || 'general'}
              onChange={setDomainId}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <SelectField
              label="Implementation Stack"
              required
              options={implementationStacks}
              value={implementationStack}
              onChange={setImplementationStack}
              error={errors.implementationStack}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 5 }}>
            <TextField
              label="Implementation Class"
              value={implementationClass}
              required
              error={errors.implementationClass}
              onChange={setImplementationClass}
              type="text"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <TextField
              label="Instance Icon"
              value={instanceIcon}
              required
              error={errors.instanceIcon}
              onChange={setInstanceIcon}
              type="text"
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="secondary"
          variant="scrollable"
          sx={{ minHeight: 40 }}
        >
          <Tab
            value={'consumedEvents'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'consumedEvents' ? 700 : 500 }}>
                Events Consumed
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          <Tab
            value={'emittedEventDefs'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'emittedEventDefs' ? 700 : 500 }}>
                Events Emitted
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          <Tab
            value={'settingDefs'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'settingDefs' ? 700 : 500 }}>
                Settings
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
        </Tabs>
        <Button
          variant="text"
          color="error"
          size="small"
          onClick={deleteAgentDef}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Delete Definition
        </Button>
      </Stack>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TabPanel value="consumedEvents" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <ConsumedEvents
            consumedEvents={consumedEvents}
            onAddEvent={addConsumedEvent}
            onDeleteEvent={deleteConsumedEvent}
          />
        </TabPanel>
        <TabPanel value="emittedEventDefs" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <EmittedEventDefs
            emittedEventDefs={emittedEventDefs}
            onAddCustomEvent={addCustomEvent}
            onDeleteCustomEvent={deleteCustomEvent}
          />
        </TabPanel>
        <TabPanel value="settingDefs" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <SettingDefs
            settingDefs={settingDefs}
            onAddSettingDef={addSettingDef}
            onUpdateSettingDef={updateSettingDef}
            onDeleteSettingDef={deleteSettingDef}
            onAddNewSection={onAddNewSection}
            onUpdateSectionName={onUpdateSectionName}
            onDeleteSection={onDeleteSection}
          />
        </TabPanel>
      </Box>

      {/* Footer Action Bar */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        <Button size="small" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          disabled={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={submitAgent}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
        >
          Save Definition
        </Button>
      </Stack>
    </Box>
  );
};

export default AgentDef;
